import ReportSectionCard from './shared/ReportSectionCard.component'
import ReportSectionHeading from '@components/pages/Reports/shared/ReportSectionHeading.component'
import React, { useMemo, useState } from 'react'
import ReportViewTabs from '@components/pages/Reports/shared/ReportViewTabs.component'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import ReportDataTable, {
  ReportDataTableColumn,
} from '@components/pages/Reports/shared/ReportDataTable.component'
import ReportFullViewTableTitle, {
  useReportFullViewSection,
} from '@components/pages/Reports/shared/ReportFullViewTableTitle.component'
import ReportExpandableSection from '@components/pages/Reports/shared/ReportExpandableSection.component'
import ReportBundleStickyTopWhenExpanded from '@components/pages/Reports/shared/ReportBundleStickyTopWhenExpanded.component'
import ReportFilterToolbar from '@components/pages/Reports/shared/ReportFilterToolbar.component'
import ReportKpiGrid from '@components/pages/Reports/shared/ReportKpiGrid.component'
import ReportPageHeader from '@components/pages/Reports/shared/ReportPageHeader.component'
import { REPORTS_DISTRIBUTION_VIEW_TABS } from '@constants/Reports/patientBaseHealth.constants'
import {
  NoShowPatientRow,
  RepeatOffenderRow,
  SCHEDULE_RELIABILITY_FILTERS,
  SCHEDULE_RELIABILITY_KPI_DEFINITIONS,
} from '@constants/Reports/scheduleReliability.constants'
import {
  ReportsDistributionView,
  ReportsKpiMetric,
} from '@dataTypes/reports.type'
import { useReportFinanceUiEffects } from '@hooks/reports/useReportFinanceUiEffects'
import { useScheduleReliability } from '@hooks/reports/useScheduleReliability'
import { formatCount } from '@utils/dashboardFinance.util'
import {
  hasNonZeroDashboardValues,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import { formatKpiPercent } from '@utils/reports/revenueRealizationFinance.util'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from '@components/pages/Reports/shared/ReportDesignSystemCharts.component'

const REPORT_CHART_AXIS_TICK = { fontSize: 12, fill: 'var(--gray-500)' }
const REPORT_CHART_TOOLTIP_STYLE = {
  borderRadius: 8,
  border: '1px solid var(--gray-200)',
  fontSize: 12,
}
const REPORT_CHART_GRID_STROKE = 'var(--gray-100)'
const REPORT_LINE_CHART_MARGIN = { top: 8, right: 12, left: 4, bottom: 2 }
const REPORT_BAR_CHART_MARGIN = { top: 24, right: 12, left: 4, bottom: 0 }
const REPORT_BAR_CATEGORY_GAP = '16%'
const REPORT_YAXIS_WIDTH = 46

function formatApptDate(value: string): string {
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function formatApptDateShort(value: string): string {
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function computePercentAxis(values: number[]): {
  domain: [number, number]
  ticks: number[]
} {
  const max = values.length ? Math.max(...values, 0) : 0
  const axisMax = max <= 0 ? 15 : Math.min(100, Math.ceil((max * 1.15) / 5) * 5)
  const step = axisMax <= 15 ? 2.5 : axisMax <= 25 ? 5 : axisMax <= 50 ? 10 : 20
  const ticks: number[] = []
  for (let t = 0; t <= axisMax; t += step) ticks.push(Number(t.toFixed(1)))
  return { domain: [0, axisMax], ticks }
}

function computeCountAxis(values: number[]): {
  domain: [number, number]
  ticks: number[]
} {
  const max = values.length ? Math.max(...values, 0) : 0
  const axisMax = max <= 0 ? 10 : Math.ceil((max * 1.15) / 10) * 10
  const step = axisMax <= 20 ? 5 : axisMax <= 50 ? 10 : Math.ceil(axisMax / 4)
  const ticks: number[] = []
  for (let t = 0; t <= axisMax; t += step) ticks.push(t)
  return { domain: [0, axisMax], ticks }
}

function ScheduleReliabilityReport(): React.ReactElement {
  const [cancellationWindowView, setCancellationWindowView] =
    useState<ReportsDistributionView>('Count')
  const [reportSaveViewAction, setReportSaveViewAction] =
    useState<JSX.Element | null>(null)
  const [reportGeneratePDFAction, setReportGeneratePDFAction] =
    useState<JSX.Element | null>(null)
  // `reportFiltersAndMetrics` is the source of truth for the filters and metrics of the currently selected report view.
  // Use this for all data queries and any logic that depends on the selected report view.
  const [reportFiltersAndMetrics, setReportFiltersAndMetrics] = useState<{
    filters: Record<string, boolean>
    metrics: Record<string, boolean>
  } | null>(null)
  const repeatOffendersFullView = useReportFullViewSection('sr-repeat')
  const noShowListFullView = useReportFullViewSection('sr-noshow')
  const { loading, error, data } = useScheduleReliability(
    (reportFiltersAndMetrics?.filters ?? {}) as unknown as Record<
      string,
      string
    >,
  )
  useReportFinanceUiEffects({
    loading,
    error,
    errorMessage: 'Failed to load schedule reliability data. Please try again.',
  })

  const kpiItems = useMemo((): ReportsKpiMetric[] => {
    if (!data) {
      return SCHEDULE_RELIABILITY_KPI_DEFINITIONS.map((def) => ({
        id: def.id,
        title: def.title,
        footer: def.footer,
        unitInline: def.unitInline,
        value: loading ? '…' : '—',
      }))
    }

    const valueById: Record<string, number> = {
      'no-show-rate': data.noShowRate.value,
      'cancellation-rate': data.cancellationRate.value,
      'no-show-count': data.noShowCount.value,
      'repeat-offenders': data.repeatOffenders.value,
    }

    return SCHEDULE_RELIABILITY_KPI_DEFINITIONS.map((def) => {
      const raw = valueById[def.id]
      const value =
        def.format === 'percent' ? formatKpiPercent(raw) : formatCount(raw ?? 0)
      return {
        id: def.id,
        title: def.title,
        footer: def.footer,
        unitInline: def.format === 'percent' ? undefined : def.unitInline,
        value,
      }
    })
  }, [data, loading])

  const trendData = useMemo(() => {
    if (!data) return []
    return data.noShowCancellationTrend.buckets.map((row) => ({
      month: row.label,
      noShowRate: row.noShowRate,
      cancellationRate: row.cancellationRate,
    }))
  }, [data])

  const trendYAxis = useMemo(() => {
    const values = trendData.flatMap((row) => [
      row.noShowRate,
      row.cancellationRate,
    ])
    return computePercentAxis(values)
  }, [trendData])

  const repeatOffenderRows = useMemo((): RepeatOffenderRow[] => {
    if (!data) return []
    return data.repeatOffenderRows.rows.map((row) => ({
      id: row.patientId,
      patientName: row.patientName,
      noShows: row.noShows,
      eligible: row.cancellations,
      completed: formatApptDate(row.lastEventDate),
    }))
  }, [data])

  const repeatOffenderColumns: ReportDataTableColumn<RepeatOffenderRow>[] =
    useMemo(() => {
      const cardColumns: ReportDataTableColumn<RepeatOffenderRow>[] = [
        { key: 'patientName', label: 'Patient Name' },
        {
          key: 'noShows',
          label: 'No-Shows',
          headerClassName: 'reports-patient-value-revenue__table-head--num',
          cellClassName: 'reports-patient-value-revenue__table-cell--num',
        },
        {
          key: 'eligible',
          label: 'Cancellations',
          headerClassName: 'reports-patient-value-revenue__table-head--num',
          cellClassName: 'reports-patient-value-revenue__table-cell--num',
        },
      ]
      if (!repeatOffendersFullView.isFullView) return cardColumns
      return [...cardColumns, { key: 'completed', label: 'Last Event' }]
    }, [repeatOffendersFullView.isFullView])

  const noShowPatientRows = useMemo((): NoShowPatientRow[] => {
    if (!data) return []
    return data.noShowPatientRows.rows.map((row) => ({
      id: row.appointmentId,
      patientName: row.patientName,
      date: formatApptDateShort(row.apptDate),
      provider: row.provider,
      procedure: row.procedure,
      phone: row.phone,
    }))
  }, [data])

  const noShowPatientColumns: ReportDataTableColumn<NoShowPatientRow>[] =
    useMemo(
      () => [
        { key: 'patientName', label: 'Patient Name' },
        { key: 'date', label: 'Date' },
        { key: 'provider', label: 'Provider' },
        { key: 'procedure', label: 'Procedure' },
        { key: 'phone', label: 'Phone' },
      ],
      [],
    )

  const cancellationWindowChartData = useMemo(() => {
    if (!data) return []
    return data.cancellationWindowDistribution.buckets.map((row) => ({
      ...row,
      barValue: cancellationWindowView === 'Count' ? row.count : row.percentage,
      topLabel:
        cancellationWindowView === 'Count'
          ? String(row.count)
          : `${row.percentage}%`,
    }))
  }, [cancellationWindowView, data])

  const cancellationWindowYAxis = useMemo(() => {
    if (cancellationWindowView === 'Count') {
      const values = cancellationWindowChartData.map((row) => row.count)
      return {
        ...computeCountAxis(values),
        tickFormatter: (v: number) => String(v),
      }
    }
    const values = cancellationWindowChartData.map((row) => row.percentage)
    return {
      ...computePercentAxis(values),
      tickFormatter: (v: number) => `${v}%`,
    }
  }, [cancellationWindowChartData, cancellationWindowView])

  const kpiEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasNonZeroDashboardValues(
      data?.noShowRate.value,
      data?.cancellationRate.value,
      data?.noShowCount.value,
      data?.repeatOffenders.value,
    ),
  )
  const trendEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.noShowCancellationTrend?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.noShowRate, row.cancellationRate),
    ),
  )
  const repeatOffendersEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.repeatOffenderRows?.rows ?? []).some((row) =>
      hasNonZeroDashboardValues(row.noShows, row.cancellations),
    ),
  )
  const noShowListEmpty = shouldShowDashboardSectionEmpty(
    loading,
    noShowPatientRows.length > 0,
  )
  const cancellationWindowEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.cancellationWindowDistribution?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.count, row.percentage),
    ),
  )

  return (
    <div className="reports-patient-base-health reports-schedule-reliability">
      <ReportBundleStickyTopWhenExpanded>
        <ReportPageHeader title="Schedule Reliability">
          {reportSaveViewAction}
          {reportGeneratePDFAction}
        </ReportPageHeader>

        <ReportFilterToolbar
          filters={SCHEDULE_RELIABILITY_FILTERS}
          onReportSaveViewActionChange={setReportSaveViewAction}
          onReportGeneratePDFActionChange={setReportGeneratePDFAction}
          onReportFiltersAndMetricsChange={setReportFiltersAndMetrics}
        />
      </ReportBundleStickyTopWhenExpanded>

      <ReportExpandableSection sectionId="sr-kpi">
        {kpiEmpty ? (
          <DashboardSectionEmptyState />
        ) : (
          <ReportKpiGrid items={kpiItems} />
        )}
      </ReportExpandableSection>

      <div className="reports-schedule-reliability__stack">
        <div className="reports-schedule-reliability__grid">
          <ReportExpandableSection sectionId="sr-trend">
            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column">
                <div className="reports-patient-base-health__chart-head">
                  <ReportSectionHeading>
                    No-show &amp; Cancellation Rate (Trend)
                  </ReportSectionHeading>
                </div>
                <p className="reports-schedule-reliability__chart-note">
                  Rates based on scheduled appointments in period
                </p>
                {trendEmpty ? (
                  <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
                ) : (
                  <div className="reports-schedule-reliability__chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={trendData}
                        margin={REPORT_LINE_CHART_MARGIN}
                      >
                        <CartesianGrid
                          vertical={false}
                          strokeDasharray="0"
                          stroke={REPORT_CHART_GRID_STROKE}
                        />
                        <XAxis
                          dataKey="month"
                          axisLine={false}
                          tickLine={false}
                          tick={REPORT_CHART_AXIS_TICK}
                        />
                        <YAxis
                          domain={trendYAxis.domain}
                          ticks={trendYAxis.ticks}
                          tickFormatter={(v) => `${v}%`}
                          axisLine={false}
                          tickLine={false}
                          tick={REPORT_CHART_AXIS_TICK}
                          width={REPORT_YAXIS_WIDTH}
                        />
                        <Tooltip
                          formatter={(value: number) => `${value}%`}
                          contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                        />
                        <Legend
                          iconType="circle"
                          iconSize={8}
                          wrapperStyle={{ fontSize: 14, paddingTop: 0 }}
                        />
                        <Line
                          type="linear"
                          dataKey="noShowRate"
                          name="No-show"
                          stroke="var(--blue-500)"
                          strokeWidth={2}
                          dot={false}
                          activeDot={false}
                        />
                        <Line
                          type="linear"
                          dataKey="cancellationRate"
                          name="Cancel"
                          stroke="var(--teal-400)"
                          strokeWidth={2}
                          dot={false}
                          activeDot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </ReportSectionCard>
          </ReportExpandableSection>

          <ReportExpandableSection sectionId="sr-repeat">
            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column reports-patient-value-revenue__column--fill">
                <div className="reports-patient-base-health__chart-head">
                  <ReportFullViewTableTitle
                    sectionId="sr-repeat"
                    title="Repeat Offenders"
                    disableEnter={repeatOffendersEmpty}
                  />
                </div>
                <p className="reports-schedule-reliability__chart-note">
                  Patient with 2+ no-shows or cancellations
                </p>
                {repeatOffendersEmpty ? (
                  <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
                ) : (
                  <div className="reports-schedule-reliability__table-block">
                    <ReportDataTable
                      key={
                        repeatOffendersFullView.isFullView
                          ? 'sr-repeat-fv'
                          : 'sr-repeat-card'
                      }
                      columns={repeatOffenderColumns}
                      rows={repeatOffenderRows}
                      rowKey={(row) => row.id}
                      initialPageSize={5}
                      className={[
                        'reports-patient-value-revenue__provider-table',
                        'reports-schedule-reliability__repeat-table',
                        repeatOffendersFullView.isFullView
                          ? 'reports-patient-value-revenue__provider-table--full-view'
                          : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      fullViewSectionId="sr-repeat"
                      fullViewSearchWithFilter
                    />
                  </div>
                )}
              </div>
            </ReportSectionCard>
          </ReportExpandableSection>
        </div>

        <div className="reports-schedule-reliability__grid">
          <ReportExpandableSection sectionId="sr-noshow">
            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column reports-patient-value-revenue__column--fill">
                <div className="reports-patient-base-health__chart-head">
                  <ReportFullViewTableTitle
                    sectionId="sr-noshow"
                    title="No-show Patient List"
                    disableEnter={noShowListEmpty}
                  />
                </div>
                <p className="reports-schedule-reliability__chart-note">
                  Most recent no-shows with contact info
                </p>
                {noShowListEmpty ? (
                  <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
                ) : (
                  <div className="reports-schedule-reliability__table-block">
                    <ReportDataTable
                      key={
                        noShowListFullView.isFullView
                          ? 'sr-noshow-fv'
                          : 'sr-noshow-card'
                      }
                      columns={noShowPatientColumns}
                      rows={noShowPatientRows}
                      rowKey={(row) => row.id}
                      initialPageSize={5}
                      className={[
                        'reports-patient-value-revenue__provider-table',
                        'reports-schedule-reliability__no-show-table',
                        noShowListFullView.isFullView
                          ? 'reports-patient-value-revenue__provider-table--full-view'
                          : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      fullViewSectionId="sr-noshow"
                      fullViewSearchWithFilter
                    />
                  </div>
                )}
              </div>
            </ReportSectionCard>
          </ReportExpandableSection>

          <ReportExpandableSection sectionId="sr-cancel">
            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column">
                <div className="reports-patient-base-health__chart-head">
                  <ReportSectionHeading>
                    Cancellation Window Distribution
                  </ReportSectionHeading>
                  {cancellationWindowEmpty ? null : (
                    <ReportViewTabs
                      options={REPORTS_DISTRIBUTION_VIEW_TABS}
                      value={cancellationWindowView}
                      onChange={(value) =>
                        setCancellationWindowView(
                          value as ReportsDistributionView,
                        )
                      }
                      className="reports-patient-base-health__view-group"
                    />
                  )}
                </div>
                <p className="reports-schedule-reliability__chart-note">
                  Top cancellation windows
                </p>
                {cancellationWindowEmpty ? (
                  <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
                ) : (
                  <div className="reports-schedule-reliability__chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={cancellationWindowChartData}
                        margin={REPORT_BAR_CHART_MARGIN}
                        barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                      >
                        <CartesianGrid
                          vertical={false}
                          strokeDasharray="0"
                          stroke={REPORT_CHART_GRID_STROKE}
                        />
                        <XAxis
                          dataKey="label"
                          axisLine={false}
                          tickLine={false}
                          tick={REPORT_CHART_AXIS_TICK}
                        />
                        <YAxis
                          domain={cancellationWindowYAxis.domain}
                          ticks={cancellationWindowYAxis.ticks}
                          tickFormatter={cancellationWindowYAxis.tickFormatter}
                          axisLine={false}
                          tickLine={false}
                          tick={REPORT_CHART_AXIS_TICK}
                          width={REPORT_YAXIS_WIDTH}
                        />
                        <Tooltip
                          formatter={(value: number) =>
                            cancellationWindowView === 'Count'
                              ? value
                              : `${value}%`
                          }
                          contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                        />
                        <Bar
                          dataKey="barValue"
                          fill="var(--blue-400)"
                          radius={[8, 8, 8, 8]}
                          barSize={100}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </ReportSectionCard>
          </ReportExpandableSection>
        </div>
      </div>
    </div>
  )
}

export default ScheduleReliabilityReport
